import { deriveAgreementSignatory } from '@statura/utils/representative';
import { isSahMeansBasisComplete } from '@statura/utils/sah';
import { recommendedSahConsentsMissing } from '@statura/utils/sah-consent';
import {
  SAH_HELD_BY_HEAD_PROVIDER_LABEL,
  sahRecordDeliveryStatusLabel,
} from '@/lib/sah/associated-head-provider';
import {
  CREATE_SERVICE_AGREEMENT_ACTION,
  isAgreementReadinessBlockerKey,
  type CreateServiceAgreementAction,
} from '@/components/clients/sah-service-agreement-dialog-helpers';
import { carePlanDetailHref, clientJourneyHref } from '@/lib/sah/client-care-plan-navigation';
import {
  sahClientBudgetReconciliationHref,
  sahClientClassificationEditorHref,
  sahClientDetailsHref,
  sahClientDecisionMakersHref,
  sahClientEvidenceAuthorityHref,
  sahClientCreateAgreementHref,
  sahClientDeliveryReadinessHref,
  sahClientOnboardingPackHref,
  sahClientReadinessHref,
  sahClientRestorativeCareHref,
} from '@/lib/sah/sah-href-contract';
import {
  sahBudgetFundingHref,
  sahBudgetFundingSetupHref,
} from '@/lib/sah/unified-sah-action-queue';
import {
  resolveSahCarePlanWorkspaceHref,
  sahCarePlanReturnContext,
  sahCarePlanStartHref,
  type SahCarePlanNavigationOptions,
} from '@/lib/sah/sah-care-plan-start';
import type { AssessmentSummaryView, SahPanelModel } from '@/components/clients/useSahBudgetModel';
import { providerDeliveryReadiness } from '@/lib/sah/provider-delivery-readiness';
import { SAH_BUDGET_PROVISION_TIMELINE_DETAIL } from '@/lib/sah/sah-budget-provision-copy';
import {
  buildUnifiedSahActionQueue,
  type PackQueueSummary,
  type UnifiedSahAction,
  type UnifiedSahActionNavigation,
  type UnifiedSahWorkflowContext,
} from '@/lib/sah/unified-sah-action-queue';

export type LifecycleStep = {
  key: string;
  label: string;
  complete: boolean;
  detail: string;
  href?: string;
  actionKey?: CreateServiceAgreementAction;
};

export type SahActionItem = {
  id: string;
  title: string;
  detail: string;
  href?: string;
  actionKey?: CreateServiceAgreementAction;
  owner: string;
  source: string;
  /** Machine-readable queue provenance for UAT/debug attributes — not coordinator UI. */
  sourceKey?: string;
  severity: 'blocker' | 'review' | 'watch';
};

export type SahJourneyPhase = {
  key: string;
  label: string;
  complete: boolean;
  detail: string;
  step: LifecycleStep;
  primaryAction: {
    href?: string;
    actionKey?: CreateServiceAgreementAction;
    label: string;
  };
};

const SAH_LIFECYCLE_ASSESSMENT_TYPES = new Set([
  'ACAT',
  'COMPREHENSIVE_CARE',
  'FUNCTIONAL',
  'IAT',
  'INITIAL',
  'NSAF',
  'REASSESSMENT',
  'REVIEW',
  'SUPPORT_NEEDS',
]);

function isCompletedAssessment(assessment: AssessmentSummaryView): boolean {
  return (
    ['COMPLETED', 'SIGNED_OFF'].includes(String(assessment.status).toUpperCase()) ||
    Boolean(assessment.completed_at)
  );
}

export function isSahLifecycleAssessment(
  assessment: Pick<AssessmentSummaryView, 'assessment_type'>,
): boolean {
  const assessmentType = String(assessment.assessment_type || '').toUpperCase();
  return SAH_LIFECYCLE_ASSESSMENT_TYPES.has(assessmentType);
}

function addDaysYmd(ymd: string, days: number): string {
  const date = new Date(`${ymd}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatReviewYmd(ymd: string, timezone: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('en-AU', {
    timeZone: timezone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatRestorativeDate(value: string, timezone: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-AU', {
    timeZone: timezone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function actionSeverityClass(severity: SahActionItem['severity']): string {
  if (severity === 'blocker') return 'border-amber-300 bg-amber-50 text-amber-950';
  if (severity === 'review') return 'border-sky-200 bg-sky-50 text-sky-950';
  return 'border-slate-200 bg-slate-50 text-slate-800';
}

export function actionSeverityLabel(severity: SahActionItem['severity']): string {
  if (severity === 'blocker') return 'Blocking';
  if (severity === 'review') return 'Needs review';
  return 'Watch';
}

export function sahLifecycleProgress(steps: readonly LifecycleStep[]): {
  completedCount: number;
  totalCount: number;
} {
  return {
    completedCount: steps.filter((step) => step.complete).length,
    totalCount: steps.length,
  };
}

export function buildSahLifecycleSteps(
  data: SahPanelModel,
  clientId: string,
  canManageServiceAgreement: boolean,
  packSummary?: PackQueueSummary | null,
  navigationOptions?: SahCarePlanNavigationOptions,
): LifecycleStep[] {
  const hasClassification =
    data.classifications.length > 0 ||
    data.classificationLevel != null ||
    data.transitionedHcpLevel != null;
  const hasMeans = isSahMeansBasisComplete(
    data.means.dbTier,
    data.means.assessedIndependence,
    data.means.assessedEverydayLiving,
  );
  const hasServiceArea = Boolean(data.serviceAreaId);
  const hasCompletedAssessment = data.assessments.some(
    (assessment) => isCompletedAssessment(assessment) && isSahLifecycleAssessment(assessment),
  );
  const missingRecommendedConsents = recommendedSahConsentsMissing(data.consents);
  const agreementSignatory = deriveAgreementSignatory({
    capacity: data.capacity,
    participantName: data.participantName,
    decisionMakers: data.decisionMakers.filter((decisionMaker) => decisionMaker.isActive),
  });
  const hasAgreementAuthority =
    agreementSignatory.kind === 'PARTICIPANT' ||
    (agreementSignatory.kind === 'SUBSTITUTE_DECISION_MAKER' && agreementSignatory.contactLinked);
  const currentSupportPlanStatus = data.currentSupportPlanId
    ? String(
        data.carePlans.find((plan) => plan.id === data.currentSupportPlanId)?.status ?? '',
      ).toUpperCase()
    : null;
  const currentSupportPlanIsActive = currentSupportPlanStatus === 'ACTIVE';
  const currentSupportPlanIsDraft = currentSupportPlanStatus === 'DRAFT';
  const currentSupportPlanNeedsAuthoring =
    currentSupportPlanStatus === 'DRAFT' ||
    currentSupportPlanStatus === 'IN_REVIEW' ||
    currentSupportPlanStatus === 'PENDING_APPROVAL';
  const classificationHref = sahClientClassificationEditorHref(clientId);
  const decisionMakersHref = sahClientDecisionMakersHref(clientId);
  const evidenceAuthorityHref = sahClientEvidenceAuthorityHref(clientId);
  const intakeHref = !hasServiceArea
    ? sahClientDetailsHref(clientId)
    : !hasClassification
      ? classificationHref
      : sahBudgetFundingSetupHref(clientId);
  // An associated provider's brokered client: the head provider holds the
  // service area, classification and contribution basis, so intake is not
  // this provider's open stage (the Readiness register already shows it Done).
  const intakeHeldByHeadProvider = data.heldByHeadProvider === true;
  const intakeDetail = intakeHeldByHeadProvider
    ? SAH_HELD_BY_HEAD_PROVIDER_LABEL
    : !hasServiceArea
      ? 'Service area allocation missing'
      : !hasClassification
        ? 'Classification missing'
        : hasMeans
          ? 'Funding basis recorded'
          : 'Contribution basis missing';
  const planHref = data.currentSupportPlanId
    ? resolveSahCarePlanWorkspaceHref(data, clientId, data.currentSupportPlanId, navigationOptions)
    : sahCarePlanStartHref(clientId);
  const providerDelivery = providerDeliveryReadiness(data);

  const coreSteps: LifecycleStep[] = [
    {
      key: 'intake',
      label: 'Intake & funding',
      complete:
        intakeHeldByHeadProvider || Boolean(hasServiceArea && hasClassification && hasMeans),
      detail: intakeDetail,
      href: intakeHref,
    },
    {
      key: 'authority',
      label: 'Evidence & authority',
      complete: hasAgreementAuthority,
      detail: hasAgreementAuthority
        ? missingRecommendedConsents.length === 0
          ? 'Consents and agreement authority in place'
          : `${missingRecommendedConsents.length} recommended consent item${
              missingRecommendedConsents.length === 1 ? '' : 's'
            } still advisory`
        : 'Agreement authority missing',
      href: hasAgreementAuthority ? evidenceAuthorityHref : decisionMakersHref,
    },
    {
      key: 'assessments',
      label: 'Assessments',
      complete: hasCompletedAssessment,
      detail: hasCompletedAssessment ? 'Assessment evidence available' : 'Complete provider intake',
      href: `/clients/${clientId}/assessments`,
    },
    {
      key: 'care-plan',
      label: 'Care plan & services',
      complete: Boolean(
        data.currentSupportPlanId && currentSupportPlanIsActive && data.hasCurrentServiceInclusions,
      ),
      detail: data.currentSupportPlanId
        ? currentSupportPlanIsDraft
          ? 'Continue building the support plan'
          : currentSupportPlanNeedsAuthoring
            ? 'Continue drafting and approve the support plan'
            : data.hasCurrentServiceInclusions
              ? 'Service inclusions linked'
              : 'Service inclusions missing'
        : 'Care plan missing',
      href: planHref,
    },
    {
      key: 'budget',
      label: 'Individualised budget',
      complete: Boolean(data.currentBudget),
      detail: data.currentBudget
        ? `$${Number(data.currentBudget.remainingAmount).toLocaleString('en-AU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} remaining`
        : SAH_BUDGET_PROVISION_TIMELINE_DETAIL,
      href: sahBudgetFundingHref(clientId),
    },
    {
      key: 'delivery',
      label: 'Delivery ready',
      complete: providerDelivery.ready,
      detail: providerDelivery.ready
        ? 'Ready for service delivery'
        : providerDelivery.blockers.length > 0
          ? `Still required: ${providerDelivery.blockers.join(', ')}`
          : data.deliveryStatusUnavailable
            ? 'Delivery readiness unavailable'
            : sahRecordDeliveryStatusLabel(data),
      href: sahClientDeliveryReadinessHref(clientId),
      ...(!data.hasSignedAgreement && canManageServiceAgreement
        ? { actionKey: CREATE_SERVICE_AGREEMENT_ACTION }
        : {}),
    },
  ];

  if (!packSummary?.hasOpenPack) {
    return coreSteps;
  }

  const packStep: LifecycleStep = {
    key: 'pack',
    label: 'Onboarding pack',
    complete: packSummary.pendingTasks <= 0,
    detail:
      packSummary.pendingTasks > 0
        ? `${packSummary.pendingTasks} onboarding pack task${
            packSummary.pendingTasks === 1 ? '' : 's'
          } still open`
        : 'Onboarding pack tasks complete',
    href: sahClientOnboardingPackHref(clientId),
  };

  return [packStep, ...coreSteps];
}

/**
 * DOM anchor ids on the Journey hub for timeline-only scroll targets. These are
 * phases whose depth panel lives ON the journey hub, so clicking scrolls to it.
 *
 * `assessments` is deliberately NOT here: assessments live on a dedicated page
 * (`/clients/{id}/assessments`), not on the hub. It was previously mis-mapped to
 * `sah-readiness`, so clicking "Assessments" scrolled to the SAH readiness
 * decision panel instead of opening the assessments workspace ("took me to the
 * decision", Amanda). With no anchor, the timeline row navigates to the phase
 * `href` instead — see the timeline-only renderer in SahClientJourneyPanel.
 */
export const SAH_JOURNEY_PHASE_ANCHOR_ID: Partial<Record<string, string>> = {
  pack: 'onboarding-pack',
  intake: 'classification-editor',
  authority: 'evidence-authority',
  delivery: 'sah-delivery-readiness',
};

export function sahJourneyPhaseAnchorId(phaseKey: string): string | null {
  return SAH_JOURNEY_PHASE_ANCHOR_ID[phaseKey] ?? null;
}

export function buildSahJourneyPhases(
  steps: LifecycleStep[],
  data: SahPanelModel,
): SahJourneyPhase[] {
  return steps.map((step) => {
    const detail =
      step.key === 'delivery' && !step.complete
        ? data.deliveryStatusUnavailable
          ? 'Delivery readiness unavailable'
          : sahRecordDeliveryStatusLabel(data)
        : step.detail;

    return {
      key: step.key,
      label: step.label,
      complete: step.complete,
      detail,
      step,
      primaryAction: {
        href: step.href,
        actionKey: step.actionKey,
        label: step.complete ? 'Review' : 'Continue',
      },
    };
  });
}

const UNIFIED_SOURCE_LABELS: Record<UnifiedSahAction['source'], string> = {
  lifecycle: 'SAH lifecycle',
  onboarding_pack: 'Onboarding pack',
  readiness_projection: 'Onboarding readiness',
  budget_workflow: 'SAH quarter budget',
  overview_clinical: 'Clinical workflow',
  finance: 'Service delivery finance',
  delivery_handoff: 'Delivery handoff',
  care_plan_review: 'Care plan review',
  restorative: 'Restorative care',
};

export function mapUnifiedSahActionToQueueItem(action: UnifiedSahAction): SahActionItem {
  return {
    id: action.id,
    title: action.title,
    detail: action.detail,
    href: action.href,
    actionKey: action.actionKey,
    owner: action.owner,
    source: UNIFIED_SOURCE_LABELS[action.source],
    sourceKey: action.source,
    severity: action.severity,
  };
}

export type ResolveSahActionQueueInput = {
  data: SahPanelModel;
  clientId: string;
  tz: string;
  plannedServicesOverBudget: boolean;
  canManageServiceAgreement: boolean;
  workflow?: UnifiedSahWorkflowContext;
  packSummary?: PackQueueSummary | null;
  navigationOptions?: SahCarePlanNavigationOptions;
  navigation?: UnifiedSahActionNavigation;
};

/** Maps the unified SAH action queue to legacy Journey panel item shape. */
export function resolveSahActionQueue(input: ResolveSahActionQueueInput): SahActionItem[] {
  return buildUnifiedSahActionQueue({
    data: input.data,
    clientId: input.clientId,
    tz: input.tz,
    plannedServicesOverBudget: input.plannedServicesOverBudget,
    canManageServiceAgreement: input.canManageServiceAgreement,
    workflow: input.workflow,
    packSummary: input.packSummary,
    navigationOptions: input.navigationOptions,
    navigation: input.navigation,
  }).map(mapUnifiedSahActionToQueueItem);
}

export function buildSahActionQueue(
  data: SahPanelModel,
  steps: LifecycleStep[],
  clientId: string,
  tz: string,
  plannedServicesOverBudget: boolean,
  canManageServiceAgreement: boolean,
): SahActionItem[] {
  const items: SahActionItem[] = [];
  const seen = new Set<string>();
  const readinessHref = sahClientReadinessHref(clientId);
  const push = (item: SahActionItem) => {
    if (seen.has(item.id)) return;
    seen.add(item.id);
    items.push(item);
  };

  for (const step of steps.filter((s) => !s.complete)) {
    push({
      id: `lifecycle-${step.key}`,
      title: step.label,
      detail: step.detail,
      href: step.href,
      actionKey: step.actionKey,
      owner:
        step.key === 'budget'
          ? 'Finance/admin'
          : step.key === 'care-plan'
            ? 'Coordinator'
            : step.key === 'assessments'
              ? 'Clinical lead'
              : 'Coordinator',
      source: 'SAH lifecycle',
      sourceKey: 'lifecycle',
      severity: ['intake', 'authority', 'care-plan', 'budget', 'delivery'].includes(step.key)
        ? 'blocker'
        : 'review',
    });
  }

  for (const blocker of data.readiness.blockers.slice(0, 6)) {
    const blockerDetail = blocker.detail || blocker.label;
    const blockerKey = String(blocker.key ?? '');
    push({
      id: `readiness-${blocker.key}`,
      title: 'Resolve readiness blocker',
      detail: blockerDetail,
      ...(blockerKey === 'signatory'
        ? { href: sahClientDecisionMakersHref(clientId) }
        : isAgreementReadinessBlockerKey(blocker.key)
          ? canManageServiceAgreement
            ? {
                actionKey: CREATE_SERVICE_AGREEMENT_ACTION,
                href: sahClientCreateAgreementHref(clientId),
              }
            : { href: readinessHref }
          : { href: readinessHref }),
      owner: 'Coordinator',
      source: 'Onboarding readiness',
      sourceKey: 'readiness_projection',
      severity: 'blocker',
    });
  }

  if (data.pendingFinancialsCount > 0) {
    push({
      id: 'pending-financials',
      title: 'Post deferred SAH financials',
      detail: `${data.pendingFinancialsCount} delivered service${
        data.pendingFinancialsCount === 1 ? '' : 's'
      } waiting for budget recovery`,
      href: sahClientBudgetReconciliationHref(clientId),
      owner: 'Finance/admin',
      source: 'Service delivery finance',
      sourceKey: 'finance',
      severity: 'review',
    });
  }

  if (plannedServicesOverBudget) {
    const overBudgetHref = data.currentSupportPlanId
      ? resolveSahCarePlanWorkspaceHref(data, clientId, data.currentSupportPlanId)
      : sahCarePlanStartHref(clientId);
    push({
      id: 'planned-over-budget',
      title: 'Planned services exceed remaining budget',
      detail:
        'Review service mix in Plan Workspace, budget, or support-plan review pathway before scheduling more funded services.',
      href: overBudgetHref,
      owner: 'Finance/admin',
      source: 'SAH quarter budget',
      sourceKey: 'budget_workflow',
      severity: 'review',
    });
  }

  if (data.activeRestorative) {
    push({
      id: 'active-restorative',
      title: 'Restorative episode active',
      detail: data.activeRestorative.max_end_at
        ? `Episode active until ${formatRestorativeDate(data.activeRestorative.max_end_at, tz)}`
        : 'Episode active with no recorded end date',
      href: sahClientRestorativeCareHref(clientId),
      owner: 'Clinical lead',
      source: 'Restorative care',
      sourceKey: 'restorative',
      severity: 'watch',
    });
  }

  const reviewWindowEnd = addDaysYmd(data.today, 14);
  const sahReviewPlans = data.carePlans
    .filter(
      (plan) =>
        plan.plan_type === 'SUPPORT_AT_HOME' &&
        ['ACTIVE', 'IN_REVIEW'].includes(String(plan.status)) &&
        Boolean(plan.review_date),
    )
    .map((plan) => ({ plan, reviewYmd: String(plan.review_date).slice(0, 10) }));
  const overdueReview =
    sahReviewPlans
      .filter((entry) => entry.reviewYmd < data.today)
      .sort((left, right) => left.reviewYmd.localeCompare(right.reviewYmd))[0] ?? null;
  const dueSoonReview =
    sahReviewPlans
      .filter(
        (entry) =>
          String(entry.plan.status) === 'ACTIVE' &&
          entry.reviewYmd >= data.today &&
          entry.reviewYmd <= reviewWindowEnd,
      )
      .sort((left, right) => left.reviewYmd.localeCompare(right.reviewYmd))[0] ?? null;
  const reviewEntry = overdueReview ?? dueSoonReview;
  if (reviewEntry) {
    const overdue = reviewEntry === overdueReview;
    const overdueCount = sahReviewPlans.filter((entry) => entry.reviewYmd < data.today).length;
    const extraOverdue = overdue && overdueCount > 1 ? ` (${overdueCount} plans overdue).` : '';
    push({
      id: 'care-plan-review',
      title: overdue ? 'Care plan review overdue' : 'Care plan review due',
      detail: overdue
        ? `Support plan review was due ${formatReviewYmd(reviewEntry.reviewYmd, tz)}. Review the plan with the participant and update it or schedule the next review.${extraOverdue}`
        : `Support plan review is due ${formatReviewYmd(reviewEntry.reviewYmd, tz)} (within 14 days).`,
      href: carePlanDetailHref(reviewEntry.plan.id, {
        hash: 'review-requests',
        returnContext: sahCarePlanReturnContext(clientId),
      }),
      owner: 'Coordinator',
      source: 'Care plan review',
      sourceKey: 'care_plan_review',
      severity: overdue ? 'review' : 'watch',
    });
  }

  const rank: Record<SahActionItem['severity'], number> = {
    blocker: 0,
    review: 1,
    watch: 2,
  };
  return items.sort((left, right) => rank[left.severity] - rank[right.severity]);
}
